import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import {
  GestureResponderEvent,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  TouchableOpacity,
  ViewStyle
} from 'react-native'
import { SvgProps } from 'react-native-svg'

import { color } from '../../../theme'

type ButtonSize = 'small' | 'medium' | 'large'
type ButtonType = 'primary' | 'outline' | 'simple'

export interface ButtonProps {
  text: string
  size: ButtonSize
  icon?: FC<SvgProps>
  onPress?: (event: GestureResponderEvent) => void
  containerStyle?: StyleProp<ViewStyle>[]
  textStyle?: StyleProp<TextStyle>[]
  iconProps?: SvgProps
  type?: ButtonType
}

export const Button = observer(
  ({
    icon,
    text,
    onPress,
    size,
    containerStyle = [],
    textStyle = [],
    type = 'primary',
    iconProps = {}
  }: ButtonProps) => {
    const disabled = !onPress
    const containerStyles = [
      styles.sizes[size].container,
      styles.types[type].container,
      icon ? withIconStyles.withIcon : {},
      disabled ? styles.types[type].disabled : {},
      { opacity: disabled ? 0.5 : 1 },
      ...containerStyle
    ]

    const calculateTextStyles = [
      styles.sizes[size].text,
      styles.types[type].text,
      { opacity: disabled ? 0.5 : 1 },
      ...textStyle
    ]
    const Icon = icon

    return (
      <TouchableOpacity onPress={onPress} style={containerStyles} disabled={disabled}>
        {Icon ? <Icon opacity={disabled ? 0.5 : 1} {...iconProps} /> : null}
        <Text allowFontScaling={false} style={calculateTextStyles}>
          {text}
        </Text>
      </TouchableOpacity>
    )
  }
)

const withIconStyles = StyleSheet.create({
  withIcon: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 15,
    gap: 10
  }
})
const smallStyles = StyleSheet.create({
  container: {
    paddingVertical: 12,
    paddingHorizontal: 20
  },
  text: {
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 16.8,
    letterSpacing: -0.5
  }
})
const mediumStyles = StyleSheet.create({
  container: {
    paddingVertical: 15,
    paddingHorizontal: 25,
    width: 120
  },
  text: {
    fontSize: 18
  }
})
const largeStyles = StyleSheet.create({
  container: {
    paddingVertical: 8,
    paddingHorizontal: 6,
    width: '100%'
  },
  text: {}
})

const styles = {
  sizes: {
    small: {
      container: {
        ...smallStyles.container
      },
      text: {
        ...smallStyles.text
      }
    },
    medium: {
      container: {
        ...mediumStyles.container
      },
      text: {
        ...mediumStyles.text
      }
    },
    large: {
      container: {
        ...largeStyles.container
      },
      text: {
        ...largeStyles.text
      }
    }
  },
  types: {
    primary: {
      container: {
        backgroundColor: color.palette.black,
        borderRadius: 50
      },
      text: {
        color: color.palette.white,
        fontWeight: 'bold',
        textAlign: 'center'
      },
      disabled: {
        backgroundColor: color.palette.lightGrey
      }
    },
    outline: {
      container: {
        borderColor: 'black',
        borderWidth: 1,
        borderRadius: 50
      },
      text: {
        color: color.primaryBlack,
        textAlign: 'center'
      },
      disabled: {}
    },
    simple: {
      container: {},
      text: {
        color: 'white',
        textAlign: 'center'
      },
      disabled: {
        opacity: 0.5
      }
    }
  }
}
